import {useEffect, useState} from "react";
import {useSocket} from "src/hooks/socket/useSocket";
import {
  QueueEnteringResponse,
  WaitingQueueInfo,
  WaitingQueueStatus,
} from "../logic/types/waitingQueue.types";
import {ConnectionState} from "src/hooks/socket/socket.type";
import {SessionAPI} from "src/services/session/logic/api/session.api";
import {SESSION_STATUS} from "src/services/session/logic/types/session.type";
import {useRegisterWaitingQueueMutation} from "../logic/quries/waitingQueue.queries";
import {isServiceUnavailableError} from "src/utils/error.utils";
import {Artist, convertArtistToType} from "src/utils/artist.utils";

const SOCKET_EMIT_EVENTS = {
  QUEUE_COMPLETE: "queue:entering",
  QUEUE_INFO: "queue:info",
};

const SOCKET_ON_EVENTS = {
  QUEUE_INFO: "queue:info",
  QUEUE_ENTERING: "queue:entering",
  QUEUE_ERROR: "queue:error",
};

type UseWaitingQueueParams = {
  artist: Artist;
  registeredQueueInfo?: WaitingQueueInfo;
  onEnterCodeAllowed?: () => void;
  onForegroundSessionNone?: () => void;
  onServiceUnavailable: () => void;
};

export const useWaitingQueue = ({
  artist,
  registeredQueueInfo,
  onEnterCodeAllowed,
  onForegroundSessionNone,
  onServiceUnavailable,
}: UseWaitingQueueParams) => {
  const {connectionState, connect, disconnect, on, off, emit} = useSocket();

  const [queueStatusData, setQueueStatusData] =
    useState<WaitingQueueInfo | null>(registeredQueueInfo ?? null);
  const [isError, setIsError] = useState<boolean>(false);

  const registerWaitingQueueMutation = useRegisterWaitingQueueMutation();

  const registerQueue = async (): Promise<void> => {
    try {
      const response = await registerWaitingQueueMutation.mutateAsync({
        data: {
          type: convertArtistToType(artist),
        },
      });
      setQueueStatusData(response);

      if (response.status === WaitingQueueStatus.ACTIVE) {
        onEnterCodeAllowed?.();
        return;
      }
    } catch (error) {
      console.log(error);
      if (isServiceUnavailableError(error)) {
        onServiceUnavailable();
        return;
      }
      throw error;
    }
  };

  const waitingQueueSocketConnect = async (): Promise<void> => {
    try {
      await connect(`${process.env.REACT_APP_SOCKET_SERVER_URL}/queue`, {
        transports: ["websocket"],
        withCredentials: true,
        reconnection: false,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const subscribeWaitingQueueSocketEvent = (): void => {
    on(SOCKET_ON_EVENTS.QUEUE_INFO, (data: unknown) => {
      console.log(`${SOCKET_ON_EVENTS.QUEUE_INFO} :: received`, data);
      const info = data as WaitingQueueInfo;
      setQueueStatusData(info);

      if (info.status === WaitingQueueStatus.ACTIVE) {
        emit(SOCKET_EMIT_EVENTS.QUEUE_COMPLETE, "");
      }
    });

    on(SOCKET_ON_EVENTS.QUEUE_ENTERING, (data: unknown) => {
      const response = data as QueueEnteringResponse;
      if (response.ok === true) {
        disconnect();
        onEnterCodeAllowed?.();
      }
    });

    on(SOCKET_ON_EVENTS.QUEUE_ERROR, (data: unknown) => {
      console.log(`${SOCKET_ON_EVENTS.QUEUE_ERROR} :: received`, data);
      setIsError(true);
    });
  };

  const unsubscribeWaitingQueueSocketEvent = (): void => {
    off(SOCKET_ON_EVENTS.QUEUE_INFO);
    off(SOCKET_ON_EVENTS.QUEUE_ENTERING);
    off(SOCKET_ON_EVENTS.QUEUE_ERROR);
  };

  // wsEligible === true일 때 소켓 연결
  useEffect(() => {
    if (queueStatusData) {
      waitingQueueSocketConnect();
    }
  }, [queueStatusData]);

  // 소켓 연결 상태가 CONNECTED일 때 자동으로 이벤트 구독
  useEffect(() => {
    if (connectionState === ConnectionState.CONNECTED) {
      subscribeWaitingQueueSocketEvent();
    }

    return () => {
      unsubscribeWaitingQueueSocketEvent();
    };
  }, [connectionState]);

  // 언마운트 될 때 disconnect 처리
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  // 포그라운드 재진입 감지 (모바일 백그라운드 복귀 + PC 탭 전환 복귀)
  const handleVisibilityChange = async (): Promise<void> => {
    if (document.visibilityState === "hidden") {
      console.log("[useWaitingQueue] 백그라운드 진입 감지");
      return;
    }

    console.log("[useWaitingQueue] 포그라운드 진입 감지 → 세션 상태 확인");

    const type = convertArtistToType(artist);
    try {
      const {status} = await SessionAPI.fetchSessionStatus({query: {type}});

      if (status === SESSION_STATUS.NONE) {
        console.log(
          "[useWaitingQueue] 세션 상태: NONE → waiting 페이지로 이동",
        );
        onForegroundSessionNone?.();
        return;
      }

      if (status === SESSION_STATUS.QUEUE) {
        console.log("[useWaitingQueue] 세션 상태: QUEUE → 소켓 재연결");
        await waitingQueueSocketConnect();
        return;
      }

      console.log(`[useWaitingQueue] 세션 상태: ${status}`);
    } catch (error) {
      if (isServiceUnavailableError(error)) {
        onServiceUnavailable();
        return;
      }
      console.error("[useWaitingQueue] 세션 상태 조회 실패", error);
    }
  };

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [onForegroundSessionNone, connect]);

  useEffect(() => {
    const interval = setInterval(() => {
      emit(SOCKET_EMIT_EVENTS.QUEUE_INFO, "");
    }, 5000);
    return () => clearInterval(interval);
  }, [queueStatusData]);

  return {
    connectionState,
    queueStatusData,
    registerQueue,
    isError,
  };
};
